import * as React from 'react';
import { useCallback } from 'react';
import styled from 'styled-components';

import { Icon } from 'components/common';
import useCarouselActions from 'components/common/Carousel/hooks/useCarouselActions';
import { Button, Alert, Label } from 'components/bootstrap';
import type { Evidence, EvidenceFilters } from 'security-app/components/Investigations/Details/TimelineSegment/types';
import { TELEMETRY_EVENT_TYPE } from 'telemetry/Constants';
import useSendTelemetry from 'logic/telemetry/useSendTelemetry';
import { EVIDENCE_CAROUSEL_ID } from 'security-app/components/Investigations/Details/TimelineSegment/Constants';
import { isPermitted } from 'util/PermissionsMixin';
import useCurrentUser from 'hooks/useCurrentUser';
import useInvestigationMessagesStreamConfig from 'security-app/components/Investigations/Details/TimelineSegment/useInvestigationMessagesStreamConfig';
import useInvestigationEventsStreamConfig from 'security-app/components/Investigations/Details/TimelineSegment/useInvestigationEventsStreamConfig';

import EvidenceCarousel from './EvidenceCarousel';

const Container = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 15px;
`;

const EvidenceCount = styled(Label)`
  display: flex;
  flex-direction: row;
  font-size: 14px;
  margin-left: 0.5rem;
`;

const CarouselWrapper = styled.div`
  display: flex;
  gap: 15px;
  flex: 1;
`;

const NavButton = styled(Button)`
  border-radius: 4px;
  padding: 10px;
  margin-top: 20px;
`;

const StyledUL = styled.ul`
  list-style-type: disc;
  margin: 10px 0;
`;

const navigationTelemetry = (direction: 'previous' | 'next') => ({
  app_pathname: 'security',
  app_section: 'investigation',
  event_details: {
    direction: direction,
  },
});

const CarouselNavigation = ({ children }: React.PropsWithChildren) => {
  const sendTelemetry = useSendTelemetry();
  const { scrollPrev, scrollNext, nextBtnDisabled, prevBtnDisabled } = useCarouselActions(EVIDENCE_CAROUSEL_ID);

  const _scrollPrev = useCallback(() => {
    sendTelemetry(
      TELEMETRY_EVENT_TYPE.SECURITY_APP.INVESTIGATION_TIMELINE_CAROUSEL_NAVIGATED,
      navigationTelemetry('previous'),
    );

    scrollPrev();
  }, [scrollPrev, sendTelemetry]);

  const _scrollNext = useCallback(() => {
    sendTelemetry(
      TELEMETRY_EVENT_TYPE.SECURITY_APP.INVESTIGATION_TIMELINE_CAROUSEL_NAVIGATED,
      navigationTelemetry('next'),
    );

    scrollNext();
  }, [scrollNext, sendTelemetry]);

  return (
    <>
      <NavButton onClick={_scrollPrev} disabled={prevBtnDisabled}>
        <Icon name="chevron_left" />
      </NavButton>
      {children}
      <NavButton onClick={_scrollNext} disabled={nextBtnDisabled}>
        <Icon name="chevron_right" />
      </NavButton>
    </>
  );
};

type Props = {
  evidenceCount: number;
  evidencesGroupedByTime: Array<[string, Array<Evidence>]>;
  onResetShowSimilar: () => void;
  onShowSimilar: (targetEvidence: Evidence) => Promise<void>;
  similarFieldsFilter: EvidenceFilters['similar'];
};

const EvidenceOverview = ({
  evidencesGroupedByTime,
  onShowSimilar,
  onResetShowSimilar,
  similarFieldsFilter,
  evidenceCount,
}: Props) => {
  const { data: messagesStreamConfig, isInitialLoading: messageStreamLoading } = useInvestigationMessagesStreamConfig();
  const { data: eventsStreamConfig, isInitialLoading: eventStreamLoading } = useInvestigationEventsStreamConfig();
  const currentUser = useCurrentUser();

  if (messageStreamLoading || eventStreamLoading) {
    return <span>Loading streams...</span>;
  }

  const hasMessageStreamAccess = isPermitted(currentUser.permissions, `streams:read:${messagesStreamConfig.stream_id}`);
  const hasEventsStreamAccess = isPermitted(currentUser.permissions, `streams:read:${eventsStreamConfig.stream_id}`);

  return (
    <Container>
      <Header>
        <h2>Evidence</h2>
        <EvidenceCount>{evidenceCount}</EvidenceCount>
      </Header>
      {(!hasMessageStreamAccess || !hasEventsStreamAccess) && (
        <Alert bsStyle="warning" title="Missing Investigation Stream Access">
          {'Your user account is missing access to the following streams, which are required to view investigation ' +
            'message and event evidence details. Please contact your administrator and request for them to share these streams with you.'}
          <StyledUL>
            {!hasMessageStreamAccess && <li>All Investigation messages</li>}
            {!hasEventsStreamAccess && <li>All Investigation events</li>}
          </StyledUL>
        </Alert>
      )}
      {!evidencesGroupedByTime.length ? (
        <Alert bsStyle="info">
          {similarFieldsFilter ? 'No similarities were found.' : 'No evidence exists for selected filters.'}
        </Alert>
      ) : (
        <CarouselWrapper>
          <CarouselNavigation>
            <EvidenceCarousel
              evidencesGroupedByTime={evidencesGroupedByTime}
              similarFieldsFilter={similarFieldsFilter}
              onResetShowSimilar={onResetShowSimilar}
              onShowSimilar={onShowSimilar}
              hasMessageStreamAccess={hasMessageStreamAccess}
              hasEventsStreamAccess={hasEventsStreamAccess}
            />
          </CarouselNavigation>
        </CarouselWrapper>
      )}
    </Container>
  );
};
export default EvidenceOverview;
